import React, { useState, useEffect } from 'react';
import { BallTriangle } from 'react-loader-spinner';
import Filter from '../Product/Filter'; // Reusing filter component
import axiosInstance from '../../axiosInstance';
import { toast } from 'react-hot-toast';
import { Avatar, Modal, TextField } from '@mui/material';
import ComingSoon from '../../components/ComingSoon';
import NoRecordFound from '../../components/NoRecordFound';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const MobileShoppers = () => {
  // All hooks at the top
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [testModeModal, setTestModeModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'reviews'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [shoppers, setShoppers] = useState([]); // Placeholder for shoppers data
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minOrder, setMinOrder] = useState('');
  const [maxOrder, setMaxOrder] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (user && !user.onboarded) setTestModeModal(true);
  }, [user]);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchData();
    } else {
      setShoppers([]); // Clear shoppers for 'reviews' tab
    }
  }, [activeTab, searchTerm]);

  const fetchData = async (page = 1, filters = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: 10,
        search: searchTerm,
        ...filters,
      };
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      const response = await axiosInstance.get('shop/shopper/get', { params });
      setShoppers(response.data.data.data);
      setPagination(response.data.data.pagination);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch shoppers');
      setShoppers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filteredShoppers = shoppers.filter(shopper =>
    (shopper.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderShopperList = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
          <BallTriangle height={50} width={55} radius={5} color="#af3585" ariaLabel="ball-triangle-loading" visible />
        </div>
      );
    }

    if (shoppers.length === 0) {
      return <NoRecordFound message="No shoppers found." />;
    }

    return shoppers.map((shopper) => (
      <div key={shopper._id} style={{ background: 'white', margin: '0 0 12px 0', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
        <Avatar
          alt={shopper.full_name}
          src={shopper.image}
          sx={{ width: 48, height: 48, marginRight: '12px', marginLeft: 0 }}
        />
        <div style={{flex: 1, minWidth: 0}}>
          <p style={{ margin: 0, fontWeight: '600', color: '#222', fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shopper.full_name || shopper.email}</p>
          <p style={{ margin: '4px 0 0', color: '#757575', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shopper.email}</p>
        </div>
        <div style={{textAlign: 'right', minWidth: 70}}>
            <p style={{ margin: 0, fontWeight: '600', color: '#222', fontSize: 15 }}>₦{shopper.price ? Number(shopper.price).toLocaleString() : 0}</p>
            <p style={{ margin: '4px 0 0', color: '#757575', fontSize: '13px' }}>Orders: {shopper.orders}</p>
        </div>
      </div>
    ));
  };

  const handleApplyFilters = () => {
    setFilterOpen(false);
    fetchData(1, {
      min_price: minPrice,
      max_price: maxPrice,
      min_order: minOrder,
      max_order: maxOrder,
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    });
  };

  if (user && !user.onboarded) {
    return (
      <Modal open={testModeModal} onClose={() => {}}>
        <div style={{
          background: 'white',
          borderRadius: 20,
          maxWidth: 350,
          margin: '100px auto',
          padding: 32,
          boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none',
        }}>
          <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Currently on Test Mode</h2>
          <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            Complete your profile to perform other transactions
          </p>
          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button
              style={{
                flex: 1,
                background: 'white',
                color: '#af3585',
                border: '2px solid #af3585',
                borderRadius: 8,
                padding: '10px 8px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginRight: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                background: '#af3585',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 7px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                marginLeft: 4
              }}
              onClick={() => navigate('/app/profile?edit=1')}
            >
              Complete Profile
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Awaiting Approval modal logic
  if (user && user.onboarded && (user.is_approved !== 'verified' || !user.is_verified)) {
    return (
      <Modal open={true} onClose={() => {}}>
        <div style={{
          background: 'white',
          borderRadius: 20,
          maxWidth: 350,
          margin: '100px auto',
          padding: 32,
          boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none',
        }}>
          <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Awaiting Verification</h2>
          <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            Your profile is currently being verified. This will take at least 24 hours.
          </p>
          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button
              style={{
                flex: 1,
                background: 'white',
                color: '#af3585',
                border: '2px solid #af3585',
                borderRadius: 8,
                padding: '10px 8px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginRight: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                background: '#af3585',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 7px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                marginLeft: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Back Home
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <div style={{ background: "white", minHeight: "100vh", paddingBottom: '80px' }}>
      <div style={{ padding: "16px 16px 10px" }}>
        <div style={{ fontWeight: 700, fontSize: 28, color: "#222" }}>Shoppers</div>
      </div>
      
      <div style={{ display: "flex", alignItems: "center", margin: "18px 16px 0" }}>
        <div style={{ flex: 1, background: "#fff", border: "1.5px solid #E0E0E0", borderRadius: 10, display: "flex", alignItems: "center", padding: "0 12px", height: 44 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="#757575" strokeWidth="2" />
            <path d="M20 20l-3-3" stroke="#757575" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: "none", outline: "none", fontSize: 16, marginLeft: 10, width: "100%", color: "#757575", background: "transparent" }}
          />
        </div>
        <div 
            onClick={() => setFilterOpen(true)}
            style={{ width: 42, height: 42, background: "#fff", border: "1.5px solid #E0E0E0", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 12, cursor: "pointer" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h16M7 12h10M10 18h4" stroke="#222" strokeWidth="2" strokeLinecap="round" />
            </svg>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginTop: 18, padding: '0 16px 12px' }}>
        <div onClick={() => handleTabChange('all')} style={{
            marginRight: 24,
            paddingBottom: 8,
            cursor: 'pointer',
            borderBottom: activeTab === 'all' ? '2px solid #AF3585' : '2px solid transparent',
            color: activeTab === 'all' ? '#222' : '#757575',
            fontWeight: activeTab === 'all' ? 700 : 500,
            fontSize: 16,
            transition: 'all 0.2s'
        }}>
            All Customers
        </div>
        <div onClick={() => handleTabChange('reviews')} style={{
            paddingBottom: 8,
            cursor: 'pointer',
            borderBottom: activeTab === 'reviews' ? '2px solid #AF3585' : '2px solid transparent',
            color: activeTab === 'reviews' ? '#222' : '#757575',
            fontWeight: activeTab === 'reviews' ? 700 : 500,
            fontSize: 16,
            transition: 'all 0.2s'
        }}>
            Customer Reviews
        </div>
      </div>
      
      {activeTab === 'all' && renderShopperList()}
      
      {activeTab === 'reviews' && (
        <ComingSoon />
      )}

      {isFilterOpen && (
        <Modal open={isFilterOpen} onClose={() => setFilterOpen(false)}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
            padding: 24,
            minWidth: 280,
            maxWidth: '90vw',
          }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Filter</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Date */}
              <div>
                <label style={{ fontSize: 14, color: '#757575' }}>Date</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <DatePicker
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Start"
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', height: 40 }}>
                    <img src="/assets/icons/line.svg" alt="to" style={{ width: 30 }} />
                  </div>
                  <DatePicker
                    selected={endDate}
                    onChange={date => setEndDate(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="End"
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              {/* Price */}
              <div>
                <label style={{ fontSize: 14, color: '#757575' }}>Price</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={minPrice}
                    onChange={e => setMinPrice(e.target.value)}
                    placeholder="Min"
                    inputProps={{ min: 0 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', height: 40 }}>
                    <img src="/assets/icons/line.svg" alt="to" style={{ width: 30 }} />
                  </div>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={maxPrice}
                    onChange={e => setMaxPrice(e.target.value)}
                    placeholder="Max"
                    inputProps={{ min: 0 }}
                  />
                </div>
              </div>
              {/* Order Unit */}
              <div>
                <label style={{ fontSize: 14, color: '#757575' }}>Order Unit</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={minOrder}
                    onChange={e => setMinOrder(e.target.value)}
                    placeholder="Min"
                    inputProps={{ min: 0 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', height: 40 }}>
                    <img src="/assets/icons/line.svg" alt="to" style={{ width: 30 }} />
                  </div>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={maxOrder}
                    onChange={e => setMaxOrder(e.target.value)}
                    placeholder="Max"
                    inputProps={{ min: 0 }}
                  />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <button onClick={handleApplyFilters} style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#af3585', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>Proceed</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

   
    </div>
  );
};

export default MobileShoppers; 